// Cio che la briscola PASSA al trasporto comune (`src/lib/gamehost/`): il `dominio` delle rotte, i posti ammessi, la mossa e come si compone l'azione, le frasi di un
// rifiuto. Il trasporto non conosce nessun gioco: e questo file che glielo dice (condizione di tma-orc su PR-A).
//
// `componiAzione` e le liste sono costanti/funzioni DI MODULO (dalla A2, #745, il trasporto tiene l'ultima callback in un ref e una lambda inline non riaprirebbe piu il socket:
// la forma di modulo resta, e non costa niente).
import { restDi } from '@/lib/gamehost/rest';
import type { TipoTavolo } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { ticketRoute } from '@/lib/gamehost/ticket';
import { MOTIVI } from './protocol';
import type { AzioneDiPersona, Carta, Posto } from './protocol';

/** Il segmento di `/v1/games/<dominio>`: `limulo-app/gioco.js` di QBriscola, `dominio`. */
export const BRISCOLA_DOMINIO = 'briscola';
/** Un contro uno, e due contro due (le coppie {0,2} e {1,3}: SOLO amichevole, ADR-0235 §11). */
export const POSTI_1V1 = 2;
export const POSTI_2V2 = 4;
export type PostiBriscola = typeof POSTI_1V1 | typeof POSTI_2V2;
/** I posti che il gioco ammette (`POSTI_AMMESSI` di `regole.ts` del game host). */
export const POSTI_AMMESSI: readonly number[] = [POSTI_1V1, POSTI_2V2];

export const BRISCOLA_REST = restDi(BRISCOLA_DOMINIO);
export const BRISCOLA_TICKET_ROUTE = ticketRoute(BRISCOLA_DOMINIO);

/** I tre livelli del robot (contratto I-833): il nome visibile e `livello.<L>` nel catalogo. */
export const LIVELLI_ROBOT = ['L0', 'L1', 'L2'] as const;
export type LivelloRobot = (typeof LIVELLI_ROBOT)[number];

/** La mossa di una persona: la carta che gioca. Il posto lo mette il trasporto, dall'ultimo `table:state`. */
export interface MossaBriscola {
  carta: Carta;
}

/** Le chiavi dell'azione sono ESATTAMENTE `t`, `posto`, `carta`: un'altra e `AZIONE_MALFORMATA` per il game host. */
export function componiAzione(posto: Posto, da: MossaBriscola): AzioneDiPersona {
  return { t: 'GIOCA', posto, carta: da.carta };
}

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO se si sceglie un livello, `robot: { livello }`. Col campo `robot` chi crea
 * siede a un posto estratto dal core e OGNI altro posto e un robot del pool (1v1: uno; 2v2: tre). Senza, nessun robot. Un tavolo coi robot e SEMPRE casual (un invito
 * non ha senso: nessun posto da riempire), e non e mai classificato.
 */
export function creaTavoloBriscolaBody(a: { tipo: TipoTavolo; posti: number; idempotencyKey: string; livello?: LivelloRobot }) {
  const base = { tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey };
  return a.livello === undefined ? base : { ...base, robot: { livello: a.livello } };
}

/** Le frasi di un rifiuto: il motivo (la carta non e in mano) e i codici delle regole e del core che ne hanno una loro. */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: MOTIVI,
  codici: ['FUORI_TURNO', 'NON_TUO', 'PRESA_DA_RACCOGLIERE', 'STALE_SEQ', 'FINITA', 'NON_DISTRIBUITO', 'THROTTLED'],
};

/**
 * Il 503 di «Contro i robot»: il core risponde `503 no_robot_available` quando il pool del gioco non ha abbastanza account liberi, e nessun tavolo nasce. Si riconosce dallo status
 * E dal codice (ovunque stia nel corpo dell'errore: `code`, `error.code`, `message`): un ALTRO 503 (il servizio che non risponde) resta un guasto generico.
 */
export function eRobotNonDisponibili(errore: unknown): boolean {
  const e = errore as { status?: unknown; data?: unknown } | null;
  if (e === null || typeof e !== 'object' || e.status !== 503) return false;
  try {
    return JSON.stringify(e.data ?? null).includes('no_robot_available');
  } catch {
    return false;
  }
}

/** La linea di un posto: nel 1v1 ogni posto e una linea, nel 2v2 le coppie sono a posti alterni (0-2 contro 1-3). */
export const lineaDelPosto = (posti: number, posto: Posto): number => (posti === POSTI_2V2 ? posto % 2 : posto);
